<p-dialog 
  [visible]="true" 
  [modal]="true" 
  [draggable]="false" 
  [resizable]="false"
  [header]="(readOnly ? 'settings_profile_view_title' : (isEditMode ? 'settings_profile_edit_title' : 'settings_profile_new_title')) | translate"
  styleClass="!rounded-[2rem] overflow-hidden"
  contentStyleClass="!p-0"
  (onHide)="close.emit()"
  [breakpoints]="{ '960px': '75vw', '640px': '90vw' }"
  [style]="{ width: '50vw' }"
>
    <!-- Content (single scroll region; footer below is a real p-dialog #footer, not nested here) -->
    <div class="px-8 py-6 max-h-[65vh] overflow-y-auto custom-scrollbar">
      <!-- Loading State -->
      <div *ngIf="loading" class="flex flex-col items-center justify-center py-20 gap-4">
        <i class="pi pi-spinner pi-spin text-4xl text-primary"></i>
        <p class="text-sm font-bold text-slate-500 tracking-tight">{{ 'loading' | translate }}...</p>
      </div>

      <form *ngIf="!loading" [formGroup]="profileForm" class="flex flex-col gap-8">

        <!-- Read-only notice -->
        <div *ngIf="readOnly" class="p-3 bg-primary/5 border border-primary/20 rounded-2xl text-primary text-xs font-bold flex items-center gap-2">
          <i class="pi pi-lock"></i>
          {{ 'settings_profile_readonly_hint' | translate }}
        </div>

        <!-- Error Message -->
        <div *ngIf="showError" class="p-4 bg-rose-50 border border-rose-100 rounded-2xl text-rose-600 text-sm font-semibold flex items-center gap-3 animate-shake">
          <i class="pi pi-exclamation-circle text-lg"></i>
          {{ 'settings_profile_error_required' | translate }}
        </div>

        <!-- Basic Info & Users -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <!-- Profile Name -->
          <div class="flex flex-col gap-2.5">
            <label class="text-sm font-bold text-slate-700 ml-1">
              {{ 'settings_profile' | translate }} <span class="text-rose-500">*</span>
            </label>
            <input
              pInputText
              formControlName="name"
              type="text"
              [readonly]="readOnly"
              [placeholder]="'settings_profile_name_placeholder' | translate"
              class="!w-full !rounded-2xl !bg-slate-50/50 !border-slate-100 !px-4 !py-3 !text-sm focus:!ring-primary/10 focus:!border-primary transition-all shadow-sm"
              [class.opacity-70]="readOnly"
            />
          </div>

          <!-- Users Multi-Select -->
          <div class="flex flex-col gap-2.5">
            <label class="text-sm font-bold text-slate-700 ml-1">
              {{ 'settings_profile_users' | translate }}
            </label>
            <p-multiSelect
              [options]="availableUsers"
              [(ngModel)]="selectedUsers"
              [ngModelOptions]="{standalone: true}"
              [disabled]="readOnly"
              optionLabel="name"
              [filter]="true"
              filterBy="name,email"
              [placeholder]="'settings_profile_select_users' | translate"
              styleClass="!w-full !rounded-2xl !bg-slate-50/50 !border-slate-100 !py-1 !text-sm focus:!ring-primary/10 focus:!border-primary transition-all shadow-sm"
              panelStyleClass="!rounded-2xl !mt-2 !shadow-2xl !border-slate-100"
              display="chip"
            >
              <!-- PrimeNG v21: named template ref (#item), not pTemplate="item" -->
              <ng-template #item let-user>
                <div class="flex items-center justify-between gap-2 py-0.5 min-w-0">
                  <div class="flex flex-col gap-0.5 min-w-0">
                    <span class="text-sm font-bold text-slate-800 truncate">{{ user.name || ('user_no_name' | translate) }}</span>
                    <span class="text-xs font-semibold text-slate-400 opacity-70 truncate">{{ user.email }}</span>
                  </div>
                  <span *ngIf="user.user_type_name"
                        [ngClass]="userTypeBadge.getClasses(user.user_type_id, user.user_type_name)"
                        class="shrink-0 px-2 py-0.5 rounded-full border text-[10px] font-black uppercase tracking-wide">
                    {{ user.user_type_name }}
                  </span>
                </div>
              </ng-template>
            </p-multiSelect>
          </div>
        </div>

        <!-- Permissions Matrix -->
        <div class="flex flex-col gap-4">
          <div class="flex items-center justify-between gap-4">
            <label class="text-lg font-black text-slate-900 tracking-tight flex items-center gap-3">
              <i class="pi pi-shield text-primary"></i>
              {{ 'settings_profile_permissions' | translate }}
            </label>
            <span class="text-xs font-bold text-slate-400 shrink-0">
              {{ grantedCount }} / {{ availablePermissions.length }}
            </span>
          </div>

          <!-- Search -->
          <div class="relative">
            <i class="pi pi-search absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 text-sm"></i>
            <input
              pInputText
              type="text"
              [(ngModel)]="permissionSearch"
              (ngModelChange)="closeHint()"
              [ngModelOptions]="{standalone: true}"
              [placeholder]="'permission_search_placeholder' | translate"
              class="!w-full !rounded-2xl !bg-slate-50/50 !border-slate-100 !pl-11 !pr-4 !py-2.5 !text-sm focus:!ring-primary/10 focus:!border-primary transition-all"
            />
          </div>

          <!-- Matrix (the toggle handlers no-op when readOnly) -->
          <div class="rounded-2xl border border-slate-100 overflow-x-auto custom-scrollbar" [class.opacity-80]="readOnly">
            <div class="min-w-[420px]">
              <!-- Header -->
              <div class="grid grid-cols-[minmax(140px,1fr)_repeat(4,64px)] bg-slate-50 border-b border-slate-100">
                <div class="px-4 py-2 text-[11px] font-black uppercase tracking-widest text-slate-400 select-none">
                  {{ 'permission_column_module' | translate }}
                </div>
                <button *ngFor="let action of permissionActions" type="button"
                  (click)="toggleColumn(action.key)"
                  [title]="action.labelKey | translate"
                  class="flex flex-col items-center justify-center gap-1 py-1.5 border-l border-slate-100 transition-colors group"
                  [ngClass]="isColumnFull(action.key) ? 'text-primary' : 'text-slate-400 hover:text-primary'">
                  <i class="pi {{ action.icon }} !text-xs"></i>
                  <span class="text-[10px] font-black uppercase tracking-wider">{{ action.labelKey | translate }}</span>
                </button>
              </div>

              <!-- Rows -->
              <ng-container *ngFor="let permission of filteredPermissions">
              <div class="grid grid-cols-[minmax(140px,1fr)_repeat(4,64px)] items-stretch border-b border-slate-50 last:border-0 transition-colors"
                   [ngClass]="isPermissionSelected(permission) ? 'bg-primary/[0.03]' : 'hover:bg-slate-50/60'"
                   [class.opacity-45]="isChildLocked(permission)">
                <!-- Module name = row toggle, + a separate info affordance (not nested in the button) -->
                <div class="flex items-center gap-1.5 w-full min-w-0 pr-1 group"
                     [ngClass]="isChild(permission) ? 'pl-9' : 'pl-4'">
                  <!-- The lock tag sits on its own line under the label, so neither ever truncates the other. -->
                  <div class="flex min-w-0 flex-1 flex-col">
                  <button type="button" (click)="toggleRow(permission)"
                    [class.cursor-not-allowed]="isChildLocked(permission)"
                    [attr.title]="isChildLocked(permission) ? ('permission_child_requires_parent_view' | translate) : null"
                    class="flex items-center gap-2.5 min-w-0 shrink py-2 text-left">
                    <!-- A child is written as an action under its parent: a tree connector instead of the dot, plain weight -->
                    <span *ngIf="!isChild(permission)" class="w-1.5 h-1.5 rounded-full shrink-0 transition-colors"
                          [ngClass]="isPermissionSelected(permission) ? 'bg-primary' : 'bg-slate-200 group-hover:bg-slate-300'"></span>
                    <span *ngIf="isChild(permission)" aria-hidden="true"
                          class="ml-0.5 h-2.5 w-2.5 shrink-0 -translate-y-1 rounded-bl-sm border-b border-l border-slate-300"></span>
                    <span class="text-sm select-none min-w-0"
                          [ngClass]="[isChild(permission) ? 'font-medium break-words' : 'font-bold truncate',
                                      isPermissionSelected(permission) ? 'text-slate-800' : 'text-slate-500']">
                      {{ rowLabelKey(permission) | translate }}
                    </span>
                  </button>
                  <span *ngIf="isChildLocked(permission)"
                        class="-mt-1.5 pb-2 pl-[22px] fb-meta select-none">
                    {{ 'permission_child_requires_parent_view' | translate }}
                  </span>
                  </div>
                  <!-- The icon is the button's whole content, so the name has to be given: the
                       panel it opens is on the body, out of reading order, and `aria-describedby`
                       is what ties the two back together while it is open. -->
                  <button *ngIf="moduleHintKey(permission)" type="button"
                    (click)="toggleHint($event, permission)"
                    [attr.aria-label]="'permission_hint_about' | translate:{ module: (rowLabelKey(permission) | translate) }"
                    [attr.aria-describedby]="hintFor?.id === permission.id ? 'permission-hint-panel' : null"
                    [attr.aria-expanded]="hintFor?.id === permission.id"
                    class="shrink-0 -my-2 inline-flex h-8 w-8 items-center justify-center text-[#64748b] hover:text-primary transition-colors"
                    [ngClass]="hintFor?.id === permission.id ? '!text-primary' : ''">
                    <i class="pi pi-info-circle !text-[14px]"></i>
                  </button>
                </div>
                <!-- Action cells. An action the module does not have is an empty cell, not a box. -->
                <ng-container *ngFor="let action of permissionActions">
                  <div *ngIf="!hasAction(permission, action.key)" class="border-l border-slate-50"></div>
                  <button *ngIf="hasAction(permission, action.key)" type="button"
                    (click)="toggleAction(permission, action.key)"
                    [class.cursor-not-allowed]="isCellInert(permission, action.key)"
                    [attr.title]="isImpliedByConsultas(permission, action.key) ? ('permission_implied_by_consultas' | translate)
                             : (isChildLocked(permission) ? ('permission_child_requires_parent_view' | translate) : null)"
                    class="flex items-center justify-center border-l border-slate-50 group">
                    <span class="w-6 h-6 rounded-lg border flex items-center justify-center transition-all"
                      [ngClass]="isActionEnabled(permission, action.key)
                        ? (isImpliedByConsultas(permission, action.key)
                            ? 'bg-slate-300 border-slate-300 text-white'
                            : 'bg-primary border-primary text-white shadow-sm')
                        : (isCellInert(permission, action.key)
                            ? 'bg-white border-slate-200 text-transparent'
                            : 'bg-white border-slate-200 text-transparent group-hover:border-primary/50')">
                      <i class="pi pi-check !text-[11px]"></i>
                    </span>
                  </button>
                </ng-container>
              </div>
              </ng-container>

              <!-- Empty state -->
              <div *ngIf="filteredPermissions.length === 0"
                   class="px-4 py-8 text-center text-sm font-semibold text-slate-400">
                {{ 'permission_search_empty' | translate }}
              </div>
            </div>
          </div>
        </div>

        <!-- ONE hint panel for the whole matrix, anchored to whichever info icon opened it and
             rendered on the body: in normal flow it changed the dialog's height, and under an
             active search the matrix is short enough that the growth moved the icon out from
             under the pointer, which closed the hint it had just opened. -->
        <p-popover #hintPanel [focusOnShow]="false" (onShow)="onHintShown()" (onHide)="onHintHidden()">
          <!-- The panel's own padding is zeroed globally (`.p-popover-content`), so every popover
               in the app pads its content here, as the other call sites do. The first line names
               the permission in full, since a child row's label is only its action. -->
          <div id="permission-hint-panel" class="max-w-[280px] px-3.5 py-3 text-[11px] text-slate-500 font-medium leading-relaxed">
            <p *ngIf="hintFor" class="mb-1 font-bold text-slate-700">{{ hintTitleKey(hintFor) | translate }}</p>
            <p>{{ hintFor && moduleHintKey(hintFor) ? (moduleHintKey(hintFor)! | translate) : '' }}</p>
          </div>
        </p-popover>
      </form>
    </div>

  <!-- Real p-dialog footer slot: rendered outside the scrolling content above -->
  <ng-template #footer>
    <div class="px-8 pt-4 pb-2 flex justify-end gap-4">
      <app-button
        [label]="(readOnly ? 'close' : 'cancel') | translate"
        variant="secondary"
        (onClick)="close.emit()"
        class="flex-1 sm:flex-initial"
      ></app-button>
      <app-button
        *ngIf="!readOnly"
        [label]="(saving ? ('saving' | translate) : ('save' | translate))"
        [loading]="saving"
        (onClick)="saveProfile()"
        class="flex-1 sm:flex-initial min-w-[120px]"
      ></app-button>
    </div>
  </ng-template>
</p-dialog>
